import React, {useEffect, useState} from 'react'
import * as a from '../'

type Props = {
  id?: string
  displayText?: string
  fieldProps?: any
  touched?: any
  errors?: any
  dropdownData: any
  verticalLabel?: boolean
  optionKey: string
  optionLabel: string
  optionGroup?: string
  optionGroupSort?: string // Added prop for sorting the option groups
  optionDisabled?: string
  selectedValue?: any | 0
  onChange?: (newValue: string) => void
  disabled?: boolean
}

export const AncientDropdown: React.FC<Props> = ({
  id,
  displayText,
  fieldProps,
  touched,
  errors,
  dropdownData,
  verticalLabel,
  optionKey,
  optionLabel,
  optionGroup,
  optionGroupSort, // Added prop
  optionDisabled,
  selectedValue,
  onChange,
  disabled,
}) => {
  const [internalSelectedValue, setInternalSelectedValue] = useState<string | undefined>(undefined)

  useEffect(() => {
    setInternalSelectedValue(selectedValue)
  }, [selectedValue])

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value
    setInternalSelectedValue(newValue)

    if (fieldProps && fieldProps.onChange) {
      fieldProps.onChange(event)
    }

    if (onChange) {
      onChange(newValue)
    }
  }

  const renderOptions = () => {
    if (!optionGroup) {
      return dropdownData?.map((setting: any, index: number) => (
        <option
          key={index}
          value={setting?.[optionKey]?.toString()}
          disabled={
            optionDisabled != undefined && setting?.[optionDisabled] != null
              ? setting?.[optionDisabled]
              : false
          }
        >
          {setting?.[optionLabel]}
        </option>
      ))
    }

    const groupedOptions: {[group: string]: JSX.Element[]} = {}
    const groupSortMap: {[group: string]: number} = {}
    const ungroupedOptions: JSX.Element[] = []
    let i = 0
    dropdownData?.forEach((setting: any) => {
      i++
      const group = setting?.[optionGroup]
      const key = setting?.[optionKey]?.toString()

      if (!group) {
        ungroupedOptions.push(
          <option
            key={key}
            value={key}
            disabled={
              optionDisabled != undefined && setting?.[optionDisabled] != null
                ? setting?.[optionDisabled]
                : false
            }
          >
            {setting?.[optionLabel]}
          </option>
        )
        return
      }

      if (!groupedOptions[group]) {
        groupedOptions[group] = []
      }
      if (optionGroupSort && groupSortMap[group] === undefined) {
        const sortValue = Number(setting?.[optionGroupSort])
        groupSortMap[group] = Number.isFinite(sortValue) ? sortValue : Number.MAX_SAFE_INTEGER
      }

      groupedOptions[group].push(
        <option
          key={key}
          value={key}
          disabled={
            optionDisabled != undefined && setting?.[optionDisabled] != null
              ? setting?.[optionDisabled]
              : false
          }
        >
          {setting?.[optionLabel]}
        </option>
      )
    })

    // Sort the option groups based on optionGroupSort prop
    const sortedGroups = Object.keys(groupedOptions).sort((a, b) => {
      if (optionGroupSort) {
        const sortA = groupSortMap[a] ?? Number.MAX_SAFE_INTEGER
        const sortB = groupSortMap[b] ?? Number.MAX_SAFE_INTEGER
        if (sortA !== sortB) return sortA - sortB
      }

      const groupA = a.toLowerCase()
      const groupB = b.toLowerCase()
      if (groupA > groupB) return -1 // Sort in descending order
      if (groupA < groupB) return 1
      return 0
    })

    return [
      ...ungroupedOptions,
      ...sortedGroups.map((group, index) => (
        <optgroup label={group} key={index}>
          {groupedOptions[group]}
        </optgroup>
      )),
    ]
  }

  const dropdownFieldProp = (
    <select
      id={id}
      className='form-select form-select-solid fw-bolder'
      data-kt-select2='true'
      data-placeholder='Select option'
      data-allow-clear='true'
      data-kt-user-table-filter='role'
      data-hide-search='true'
      onChange={handleChange}
      disabled={disabled}
      value={internalSelectedValue}
      {...fieldProps}
    >
      <option>Select an option</option>
      {renderOptions()}
    </select>
  )
  const dropdown = (
    <select
      id={id}
      className='form-select form-select-solid fw-bolder'
      data-kt-select2='true'
      data-placeholder='Select option'
      data-allow-clear='true'
      data-kt-user-table-filter='role'
      data-hide-search='true'
      onChange={handleChange}
      disabled={disabled}
      value={internalSelectedValue}
    >
      <option>Select an option</option>
      {renderOptions()}
    </select>
  )

  return verticalLabel ? (
    <a.AncientFormWrapVertical displayText={displayText}>
      {onChange ? dropdown : dropdownFieldProp}
    </a.AncientFormWrapVertical>
  ) : (
    <a.AncientFormWrapHorizontal displayText={displayText}>
      {onChange ? dropdown : dropdownFieldProp}
    </a.AncientFormWrapHorizontal>
  )
}
